'use client';

import {
  useEffect,
  useRef,
  useState
} from 'react';

import {
  createPortal
} from 'react-dom';

import type {
  PracticeFigure as Figure
} from '@/app/lib/practice/types';

// A question's diagram (spec section 2). The SVG was sanitised on the server
// against a strict allowlist when the bank loaded (svg-sanitise.ts), so it is
// safe to inline; it draws in currentColor, so it follows the text colour.
// Tap, click or Enter opens a full-screen view with zoom in and out (and
// pinch); Escape or Done closes it. `alt` is the accessible name.

const MIN_ZOOM = 60;
const MAX_ZOOM = 300;
const STEP = 40;

const BUTTON = 'min-h-[44px] px-5 rounded-full text-sm font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(1,158,250)] disabled:opacity-40';

export function PracticeFigure({
  figure
}: { figure: Figure }) {

  const [ open, setOpen ] = useState(false);
  const [ zoom, setZoom ] = useState(100);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const doneRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {

    if (!open) {

      return;
    }

    doneRef.current?.focus();
    const trigger = triggerRef.current;

    const onKey = (event: KeyboardEvent) => {

      if (event.key === 'Escape') {

        event.preventDefault();
        setOpen(false);
      }
    };

    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {

      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      trigger?.focus();
    };
  }, [ open ]);

  const openZoom = () => {

    setZoom(100);
    setOpen(true);
  };

  return (
    <figure className="m-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={openZoom}
        aria-label={`${ figure.alt } (enlarge the diagram)`}
        className="practice-figure relative block w-full cursor-zoom-in rounded-2xl bg-gray-50 dark:bg-gray-800/60 p-3.5 text-[#111111] dark:text-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(1,158,250)]"
      >
        <span
          className="block [&_svg]:block [&_svg]:w-full [&_svg]:h-auto [&_svg]:max-h-[260px] @min-[600px]/solve:[&_svg]:max-h-[340px] @min-[900px]/solve:[&_svg]:max-h-[420px]"
          dangerouslySetInnerHTML={{
            __html: figure.svgHtml
          }}
        />
        <span aria-hidden="true" className="absolute right-3 bottom-2 text-xs text-gray-400">Tap to enlarge</span>
      </button>
      {figure.captionHtml && (
        <figcaption
          className="practice-maths mt-1.5 text-xs text-gray-500 dark:text-gray-400"
          dangerouslySetInnerHTML={{
            __html: figure.captionHtml
          }}
        />
      )}

      {open && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={figure.alt}
          className="fixed inset-0 z-[200] flex flex-col bg-white dark:bg-gray-950 text-[#111111] dark:text-gray-100"
        >
          <div className="flex items-center gap-2 px-4 pt-[max(12px,env(safe-area-inset-top))] pb-3">
            <button type="button" disabled={zoom >= MAX_ZOOM} onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + STEP))} className={`${ BUTTON } bg-gray-100 dark:bg-gray-800`}>
              Zoom in
            </button>
            <button type="button" disabled={zoom <= MIN_ZOOM} onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - STEP))} className={`${ BUTTON } bg-gray-100 dark:bg-gray-800`}>
              Zoom out
            </button>
            <button ref={doneRef} type="button" onClick={() => setOpen(false)} className={`${ BUTTON } ml-auto bg-[rgb(1,158,250)] text-white`}>
              Done
            </button>
          </div>
          {/* m-auto, not justify-center: centred flex content that overflows
              cannot be scrolled to its left edge. shrink-0: without it the
              flex item shrank back to the screen and Zoom in did nothing
              wider than the viewport (phones and tablets, 9 Oct 2026). */}
          <div className="flex-1 overflow-auto flex p-4 [touch-action:pan-x_pan-y_pinch-zoom]">
            <div
              className="m-auto shrink-0 [&_svg]:block [&_svg]:h-auto [&_svg]:w-full transition-[width] duration-150 motion-reduce:transition-none"
              style={{
                width: `min(${ zoom }%, ${ 9 * zoom }px)`
              }}
              dangerouslySetInnerHTML={{
                __html: figure.svgHtml
              }}
            />
          </div>
        </div>,
        document.body
      )}
    </figure>
  );
}
